import React from 'react';
import { KatButton } from '@amzn/katal-react';
import { FunctionMetadata } from '../../../services/Batcher/BatcherService';

interface SelectFunctionsTabProps {
    chain: FunctionMetadata[];
    /** additionalArguments values per chain item, aligned by index with `chain`. */
    chainArgs: Record<string, string>[];
    /** Index of the chain item whose Additional Parameters box is open (null = none). */
    selectedChainIndex: number | null;
    loading: boolean;
    error: string;
    onOpenModal: () => void;
    onRemoveFunction: (index: number) => void;
    onSelectChainItem: (index: number) => void;
    onAdditionalArgChange: (index: number, name: string, value: string) => void;
}

/** Step 1: build the function chain and edit each function's additional parameters. */
const SelectFunctionsTab: React.FC<SelectFunctionsTabProps> = ({
    chain,
    chainArgs,
    selectedChainIndex,
    loading,
    error,
    onOpenModal,
    onRemoveFunction,
    onSelectChainItem,
    onAdditionalArgChange,
}) => {
    const selected = selectedChainIndex !== null ? chain[selectedChainIndex] : undefined;

    return (
        <div className="batcher-panel">
            <div className="batcher-panel-header">
                <span className="batcher-panel-title">Functions</span>
                <KatButton variant="secondary" onClick={onOpenModal} disabled={loading}>
                    {loading ? 'Loading...' : '+ Add a function to chain'}
                </KatButton>
            </div>

            {error && <div className="batcher-error">{error}</div>}

            {chain.length === 0 ? (
                <div className="batcher-empty">No functions added</div>
            ) : (
                <ol className="batcher-chain">
                    {chain.map((fn, index) => (
                        <li
                            key={`${fn.functionIdentifier}-${index}`}
                            className={`batcher-chain-item${selectedChainIndex === index ? ' batcher-chain-item-selected' : ''}`}
                        >
                            <div
                                className="batcher-chain-item-content"
                                role="button"
                                tabIndex={0}
                                aria-expanded={selectedChainIndex === index}
                                onClick={() => onSelectChainItem(index)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onSelectChainItem(index);
                                    }
                                }}
                            >
                                <div className="batcher-chain-item-main">
                                    <span className="batcher-chain-item-name">{fn.functionName}</span>
                                    <KatButton
                                        variant="link"
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            onRemoveFunction(index);
                                        }}
                                    >
                                        Remove
                                    </KatButton>
                                </div>
                                <div className="batcher-chain-item-fields">
                                    {fn.inputSchema && fn.inputSchema.length > 0 ? (
                                        fn.inputSchema.map(field => (
                                            <span key={field.name} className="batcher-field-chip" title={field.description}>
                                                {field.name}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="batcher-fields-label">No CSV input fields required</span>
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            )}

            {selectedChainIndex !== null && selected && (
                <div className="batcher-additional-params">
                    <div className="batcher-additional-params-title">
                        Additional Parameters — {selected.functionName}
                    </div>
                    {selected.additionalArguments && selected.additionalArguments.length > 0 ? (
                        selected.additionalArguments.map(param => (
                            <div key={param.name} className="batcher-input-field">
                                <label
                                    className="batcher-input-label"
                                    htmlFor={`batcher-arg-${selectedChainIndex}-${param.name}`}
                                >
                                    {param.name}
                                </label>
                                {param.description && <p className="batcher-input-hint">{param.description}</p>}
                                <input
                                    id={`batcher-arg-${selectedChainIndex}-${param.name}`}
                                    type="text"
                                    className="batcher-arg-input"
                                    value={(chainArgs[selectedChainIndex] || {})[param.name] || ''}
                                    onChange={e => onAdditionalArgChange(selectedChainIndex, param.name, e.target.value)}
                                />
                            </div>
                        ))
                    ) : (
                        <div className="batcher-fields-label">This function has no additional parameters.</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SelectFunctionsTab;
